// Chapter 7 - Analyzing Behavior Change: Basic Assumptions and Strategies
// Source: Cooper, Heron, & Heward (3rd ed.), Chapter 7.
// All content traced to source-pdfs/Chapter-7-Analyzing-Behavior-Change-Basic-Assumptions-and-Strategies-6.pdf

import type { ChapterModule } from "./types";

const chapter07Module: ChapterModule = {
  meta: {
    id: "07",
    number: 7,
    title: "Analyzing Behavior Change: Basic Assumptions and Strategies",
    subtitle: "Functional relations, IV/DV, single-subject logic",
    part: "Part 3 - Evaluating and Analyzing Behavior Change",
    estMinutes: 80,
    bacb: ["D-1", "D-2", "D-3", "D-4", "D-5"],
  },
  curated: true,
  overview: [
    "Chapter 7 lays the philosophical and methodological foundation for everything in Chapters 8-10. Measurement can show whether, when, and how much behavior changed; it cannot reveal WHY. To answer that, behavior analysts conduct experiments that establish a functional relation between an independent variable (IV) and a dependent variable (DV).",
    "You will learn the assumptions that guide the science (determinism, behavior as individual phenomenon, variability as environmental rather than intrinsic), the components of an ABA experiment (research question, single-case logic, DV, IV, extraneous variables, ongoing visual inspection), the steady-state strategy, baseline logic with its three elements (prediction, verification, replication), and the inductive reasoning known as affirmation of the consequent.",
  ],
  objectives: [
    { id: "D-1", text: "Distinguish between dependent and independent variables." },
    { id: "D-2", text: "Distinguish between internal and external validity." },
    { id: "D-3", text: "Identify the defining features of single-subject experimental designs." },
    { id: "D-4", text: "Describe the elements of baseline logic: prediction, verification, and replication." },
    { id: "D-5", text: "Use single-subject experimental designs to demonstrate experimental control." },
  ],
  coreConcepts: [
    {
      title: "Three goals of science (revisited)",
      body: [
        "Science produces understanding at three levels: description, prediction, and control. Descriptive research yields a collection of facts; correlation enables prediction; experimental control through manipulation of an IV enables an analyst to demonstrate a FUNCTIONAL relation between IV and DV.",
      ],
      bullets: [
        "Description: facts that can be quantified and classified.",
        "Prediction: probabilistic statements from correlation.",
        "Control: demonstration of a functional relation via manipulation.",
      ],
    },
    {
      title: "Experimental control and functional relations",
      body: [
        "Experimental control is achieved when a predictable change in the DV is reliably produced by systematic manipulation of the IV. A behavior has been ANALYZED when a reliable functional relation has been demonstrated convincingly. A functional relation does NOT rule out that other variables also influence the behavior.",
      ],
    },
    {
      title: "Internal vs external validity",
      body: [
        "Internal validity: confidence that the IV (not uncontrolled variables) caused the DV change. External validity: extent to which results generalize to other subjects, settings, or behaviors. Threats to internal validity include confounding variables (uncontrolled influences) and extraneous variables (irrelevant influences that must be held constant).",
      ],
    },
    {
      title: "Behavior assumptions guiding the analysis",
      body: [
        "ABA experimentation rests on (1) determinism (behavior is lawful), (2) behavior is an individual phenomenon (single-subject methods, each subject is own control), (3) behavior is dynamic and continuous (must be measured over time), and (4) behavioral variability is imposed by environmental variables, not an intrinsic trait. The analyst's job is to isolate and manipulate those variables.",
      ],
    },
    {
      title: "Components of an ABA experiment",
      body: [
        "Every ABA experiment includes a research question that drives all design decisions; one or more DVs (measurable dimensional quantities of the target behavior); one or more IVs (the manipulated variables); extraneous variables (held constant); a standardized measurement protocol; and ongoing visual inspection.",
      ],
      bullets: [
        "Multiple DVs add data paths, assess generalization, and check for collateral changes in others' behavior.",
        "All other aspects of the experimental setting (extraneous variables) must be held constant.",
        "If unplanned events occur, either wait out their effects or incorporate them into the design.",
        "Visual inspection is continuous, not end-of-study.",
      ],
    },
    {
      title: "Two basic IV changes",
      body: [
        "Despite the infinite design space, an experimenter can make only TWO kinds of IV change: introduce a NEW condition, or REINSTATE a previously used condition. Parametric studies compare different values of the IV. The fundamental rule is: change only one variable at a time. Designs should be improvised when needed (Skinner, 1966) rather than rigidly pro forma.",
      ],
    },
    {
      title: "Steady state strategy and baseline logic",
      body: [
        "Steady state responding (low variability under constant conditions) enables baseline logic, an inductive reasoning chain with three elements: PREDICTION (extrapolate baseline forward), VERIFICATION (show baseline would have continued without IV), and REPLICATION (reproduce the change by reintroducing IV).",
      ],
      bullets: [
        "Prediction: project the steady baseline forward as the counterfactual.",
        "Verification: when conditions return to baseline, behavior returns to baseline.",
        "Replication: reintroducing the IV reproduces the change.",
      ],
    },
    {
      title: "Baseline conditions: when to introduce the IV",
      body: [
        "Four baseline patterns: stable, ascending, descending, variable. Rules: introduce the IV when baseline is STABLE; do NOT introduce when ascending/descending shows IMPROVING performance; you may introduce when ascending/descending shows DETERIORATING performance; do NOT introduce on a highly variable, unstable baseline.",
      ],
      bullets: [
        "Baseline does not mean 'no treatment' - it means absence of the SPECIFIC IV under study.",
        "Baseline also reveals A-B-C correlations and helps set initial reinforcement criteria.",
        "Baseline may even reveal that no intervention is warranted.",
      ],
    },
    {
      title: "Affirmation of the consequent",
      body: [
        "The inductive reasoning behind single-case logic: if A (IV present) then B (behavior change). Observing B reinforces but does not prove A; some other variable could have caused B. Strong designs reduce this risk by confirming multiple if-A-then-B predictions across replications.",
      ],
      bullets: [
        "Practice effects: improvements due to repeated opportunities to perform the behavior.",
        "Extended baselines are not always needed when a behavior has no logical opportunity to occur.",
      ],
    },
  ],
  glossary: [
    { term: "Independent variable (IV)", definition: "The variable the experimenter manipulates; typically the intervention.", sourceHint: "specific manipulations of one event (the independent variable)." },
    { term: "Dependent variable (DV)", definition: "A measurable dimensional quantity of the target behavior; what the experimenter measures.", sourceHint: "The dependent variable in an applied behavior analysis experiment is a measurable dimensional quantity of the target behavior." },
    { term: "Experimental control", definition: "A predictable change in the DV that is reliably produced by systematic manipulation of the IV.", sourceHint: "Experimental control is achieved when a predictable change in behavior (the dependent variable) is reliably produced by the systematic manipulation of some aspect of the environment (the independent variable)." },
    { term: "Experimental design", definition: "The sequence and form of independent variable manipulations in a study.", sourceHint: "The term experimental design refers to the sequence and form of independent variable manipulations in a study." },
    { term: "Single-case designs", definition: "Designs that use each subject as their own control via within-subject IV manipulation.", sourceHint: "Experiments in applied behavior analysis are referred to as single-case research designs because the experimental logic for detecting and analyzing behavior change employs each subject as his or her own control." },
    { term: "Internal validity", definition: "Confidence that the IV (not uncontrolled variables) caused the DV change.", sourceHint: "An experiment that shows convincingly that changes in behavior are a function of the independent variable and not the result of uncontrolled or unknown variables has internal validity." },
    { term: "External validity", definition: "Extent to which results generalize to other subjects, settings, and/or behaviors.", sourceHint: "External validity refers to the degree to which a study's results are generalizable to other subjects, settings, and/or behaviors." },
    { term: "Confounding variable", definition: "An uncontrolled variable that exerts unknown influence on the DV.", sourceHint: "Confounding variables exert unknown or uncontrolled influences on the dependent variable." },
    { term: "Extraneous variable", definition: "Variables other than the IV that the experimenter must hold constant.", sourceHint: "the behavior analyst must hold constant all other aspects of the experimental setting - extraneous variables - to prevent unplanned environmental variation." },
    { term: "Research question", definition: "The specific question the investigator wants to answer; drives all design decisions.", sourceHint: "A research question specifies what the investigator wants to learn from conducting the experiment." },
    { term: "Parametric analysis", definition: "An experimental comparison of the differential effects of different VALUES of the same IV.", sourceHint: "A parametric study compares the differential effects of different values of the independent variable." },
    { term: "Baseline", definition: "Preintervention measurement of the target behavior used as the comparison against changes when the IV is introduced.", sourceHint: "These preintervention data serve as the baseline against which any observed changes in behavior when the independent variable is introduced can be compared." },
    { term: "Baseline logic", definition: "Inductive reasoning chain (prediction, verification, replication) used in single-case designs.", sourceHint: "Steady state responding enables a form of inductive reasoning called baseline logic. Baseline logic entails three elements: prediction, verification, and replication." },
    { term: "Steady state responding", definition: "A pattern of low variability under constant environmental conditions.", sourceHint: "Prediction of future behavior can be made when repeated measures of behavior under relatively constant environmental conditions show little or no variation." },
    { term: "Steady state strategy", definition: "Strategy of continuing measurement under constant conditions until variability is low enough that prediction is justified.", sourceHint: "Steady state responding enables a form of inductive reasoning." },
    { term: "Stable baseline", definition: "Baseline with low variability around a clear central value; suitable for introducing the IV.", sourceHint: "The independent variable should be introduced when stable baseline responding has been achieved." },
    { term: "Ascending baseline", definition: "Baseline trending upward; introduce IV only if the trend reflects deteriorating performance.", sourceHint: "The independent variable should not be introduced when an ascending or descending baseline indicates improving performance." },
    { term: "Descending baseline", definition: "Baseline trending downward; introduce IV only if the trend reflects deteriorating performance.", sourceHint: "The independent variable should be introduced when an ascending or a descending baseline indicates deteriorating performance." },
    { term: "Variable baseline", definition: "Baseline with high variability; an IV should not be imposed until variability is controlled.", sourceHint: "An independent variable should not be imposed on a highly variable, unstable baseline." },
    { term: "Verification", definition: "Demonstration that baseline-level responding would have continued unchanged had the IV not been introduced.", sourceHint: "Verification is accomplished by demonstrating that the prior level of baseline responding would have remained unchanged had the independent variable not been introduced." },
    { term: "Replication", definition: "Reproducing a previously observed behavior change by reintroducing the IV; reduces likelihood that another variable caused the original change.", sourceHint: "Replication within an experiment means reproducing a previously observed behavior change by reintroducing the independent variable." },
    { term: "Prediction (baseline logic)", definition: "Extrapolating steady-state baseline forward in time as the counterfactual against which the intervention is judged.", sourceHint: "Prediction of future behavior can be made when repeated measures of behavior under relatively constant environmental conditions show little or no variation." },
    { term: "A-B design", definition: "A simple baseline-then-treatment design; lacks the replication of A-B-A-B and is rarely sufficient for inference.", sourceHint: "A-B design (Key Terms list)." },
    { term: "Affirmation of the consequent", definition: "Inductive reasoning of the form 'if A then B; B occurred; therefore A.' Logically invalid but useful when multiple if-A-then-B predictions converge.", sourceHint: "The predictive power of steady state responding enables a form of inductive reasoning known as affirmation of the consequent." },
    { term: "Practice effects", definition: "Improvements in performance resulting from repeated opportunities to emit the behavior.", sourceHint: "Practice effects refer to improvements in performance resulting from opportunities to emit the behavior that must be provided to obtain repeated measures." },
  ],
  procedures: [
    {
      title: "Frame an ABA experiment",
      intro: "Lock in research question, variables, and controls before any data are collected.",
      steps: [
        { title: "Write the research question", detail: "Specify exactly what you want to learn; all design decisions follow from it." },
        { title: "Define the DV", detail: "Pick a measurable dimensional quantity of the target behavior; specify the measurement method (Ch 4)." },
        { title: "Define the IV", detail: "Describe the manipulated variable in enough detail to replicate it." },
        { title: "Identify extraneous variables", detail: "List anything else that could influence the DV and how it will be held constant." },
        { title: "Plan visual inspection", detail: "Decide how data will be graphed and reviewed in real time as the study runs." },
      ],
    },
    {
      title: "Apply baseline logic (prediction, verification, replication)",
      intro: "The three-step inductive chain that drives every single-case design.",
      steps: [
        { title: "Establish steady-state baseline", detail: "Collect baseline data until variability is low enough that the data path can be projected forward." },
        { title: "Predict", detail: "Extrapolate the baseline forward as the no-treatment counterfactual." },
        { title: "Introduce IV and observe DV change", detail: "Compare the new data path to the prediction." },
        { title: "Verify", detail: "Reintroduce baseline (in a reversal) or use a multiple baseline to show the behavior would have continued at baseline without the IV." },
        { title: "Replicate", detail: "Reintroduce the IV (or replicate across subjects/behaviors) to confirm the IV is the source of change." },
      ],
    },
    {
      title: "Decide when to introduce the IV",
      intro: "Read the baseline pattern.",
      steps: [
        { title: "Stable", detail: "Introduce IV - prediction is well-supported." },
        { title: "Ascending or descending toward IMPROVEMENT", detail: "Do NOT introduce IV yet - behavior is already changing in the desired direction." },
        { title: "Ascending or descending toward DETERIORATION", detail: "You may introduce IV - the trend signals worsening and helps detect a beneficial effect." },
        { title: "Variable / unstable", detail: "Do NOT introduce IV - extend baseline, investigate sources of variability, refine measurement." },
      ],
    },
  ],
  examples: [
    { scenario: "A study measures number of math problems completed per minute (DV) under two reinforcement schedules (IV).",
      application: "Classic IV/DV experiment; the analyst predicts steady-state baseline, manipulates the schedule, and watches for level/trend change.",
      sourceHint: "The dependent variable in an applied behavior analysis experiment is a measurable dimensional quantity of the target behavior." },
    { scenario: "Baseline tantrum frequency rises steadily across the first week before any intervention.",
      application: "Ascending baseline trending toward deterioration; introducing the IV now is acceptable because beneficial change will be visible against the trend.",
      sourceHint: "The independent variable should be introduced when an ascending or a descending baseline indicates deteriorating performance." },
    { scenario: "Three weeks of stable, low compliance during baseline; teacher introduces a token system; compliance jumps to 90% within two days.",
      application: "Steady-state baseline supports prediction; the dramatic level change relative to baseline supports the inference that the IV drove the change. Verification + replication still required.",
      sourceHint: "These preintervention data serve as the baseline against which any observed changes in behavior when the independent variable is introduced can be compared." },
    { scenario: "Researcher notes that during baseline a teacher unexpectedly changes seating, possibly affecting on-task time.",
      application: "Unplanned event - confounding variable. The analyst must either wait out the effect or incorporate it into the design (e.g., flag the seating change as a second IV).",
      sourceHint: "When unplanned events or variations occur in the experimental setting, the behavior analyst must either wait out their effects or incorporate them into the design of the experiment." },
  ],
  comparisons: [
    {
      title: "Description vs Prediction vs Control",
      left:  { label: "Description / Prediction", points: [
        "Description: facts about observed events.",
        "Prediction: from correlation between events.",
        "Neither establishes causation.",
      ]},
      right: { label: "Control", points: [
        "Functional relation via IV manipulation.",
        "Establishes 'X reliably produces Y.'",
        "Highest level of scientific understanding.",
      ]},
    },
    {
      title: "Internal vs External validity",
      left:  { label: "Internal validity", points: [
        "IV (not confounds) caused the DV change.",
        "Threatened by extraneous variables, practice effects, history, maturation.",
      ]},
      right: { label: "External validity", points: [
        "Generalization to other subjects, settings, or behaviors.",
        "Addressed through systematic replication.",
      ]},
    },
    {
      title: "Confounding vs Extraneous variables",
      left:  { label: "Confounding variable", points: [
        "Exerts unknown influence on the DV.",
        "Threatens internal validity.",
      ]},
      right: { label: "Extraneous variable", points: [
        "Could influence the DV but is held CONSTANT.",
        "Methodologically managed; not a threat once controlled.",
      ]},
    },
  ],
  misconceptions: [
    {
      myth: "If A-B-A baseline returns happen, the IV caused the change.",
      reality: "Strong, but still a case of affirmation of the consequent: the IV is likely the cause, but multiple replications and verifications are needed to be convincing.",
      sourceHint: "Although the logic of affirmation of the consequent is flawed (some other event may have caused the change in behavior), an effective experimental design confirms several if-A-then-B possibilities.",
    },
    {
      myth: "ABA experiments need large group samples.",
      reality: "ABA uses within-subject single-case designs because behavior is an individual phenomenon; each subject serves as their own control.",
      sourceHint: "Because behavior is an individual phenomenon, the experimental strategy of behavior analysis features within-subject (or single-case) methods of analysis.",
    },
    {
      myth: "Variability inside a baseline is acceptable as long as the mean looks right.",
      reality: "An IV should NOT be imposed on a highly variable, unstable baseline; the analyst must first understand or control the source of variability.",
      sourceHint: "An independent variable should not be imposed on a highly variable, unstable baseline.",
    },
    {
      myth: "Baseline means doing nothing.",
      reality: "Baseline only means absence of the SPECIFIC IV being studied. Other instruction and care can continue.",
      sourceHint: "A baseline condition does not necessarily mean the absence of instruction or treatment per se, only the absence of a specific independent variable of experimental interest.",
    },
    {
      myth: "Behavioral variability is something inside the organism.",
      reality: "ABA assumes variability is imposed by environmental variables. The analyst's job is to find and manipulate them.",
      sourceHint: "Experimental methods in behavior analysis also derive from the assumption that behavioral variability is extrinsic to the organism, not an inherent trait.",
    },
  ],
  examChecklist: [
    "Distinguish description, prediction, and control as scientific aims.",
    "Define IV, DV, extraneous variable, and confounding variable.",
    "Distinguish internal and external validity with one threat each.",
    "List the three elements of baseline logic.",
    "List the four baseline patterns and the IV-introduction rule for each.",
    "Define affirmation of the consequent and explain why it is logically weak but methodologically useful.",
    "Describe the two basic kinds of IV changes available to an experimenter.",
    "Explain why ABA uses single-case designs.",
    "Explain the difference between practice effects and treatment effects.",
    "Define parametric analysis with one example.",
  ],
  flashcardTerms: [
    "Independent variable (IV)", "Dependent variable (DV)",
    "Experimental control", "Experimental design", "Single-case designs",
    "Internal validity", "External validity",
    "Confounding variable", "Extraneous variable",
    "Research question", "Parametric analysis",
    "Baseline", "Baseline logic",
    "Steady state responding", "Steady state strategy",
    "Stable baseline", "Ascending baseline", "Descending baseline", "Variable baseline",
    "Verification", "Replication", "Prediction (baseline logic)",
    "A-B design", "Affirmation of the consequent", "Practice effects",
  ],
  quiz: [
    {
      id: "Q1",
      question: "Which is the HIGHEST level of scientific understanding in ABA?",
      options: [
        { label: "A", text: "Description" },
        { label: "B", text: "Correlation" },
        { label: "C", text: "Prediction" },
        { label: "D", text: "Control" },
      ],
      answer: "D",
      explanation: "Control - demonstrated by a functional relation through IV manipulation - is the highest level. Description and correlation/prediction precede it.",
      topic: "Goals of science",
      sourceHint: "The greatest potential benefits of science are derived from the third, and highest, level of scientific understanding, which comes from establishing experimental control.",
    },
    {
      id: "Q2",
      question: "Verification, in baseline logic, means demonstrating:",
      options: [
        { label: "A", text: "Reproducing the change by re-introducing the IV." },
        { label: "B", text: "That baseline would have remained unchanged without the IV." },
        { label: "C", text: "That the IV is socially valid." },
        { label: "D", text: "That observers agreed at 80%." },
      ],
      answer: "B",
      explanation: "Verification shows the prior baseline level would have continued absent the IV (e.g., by returning to baseline conditions and seeing behavior return to baseline).",
      topic: "Baseline logic",
      sourceHint: "Verification is accomplished by demonstrating that the prior level of baseline responding would have remained unchanged had the independent variable not been introduced.",
    },
    {
      id: "Q3",
      question: "When should an IV NOT be introduced?",
      options: [
        { label: "A", text: "When baseline is stable" },
        { label: "B", text: "When baseline is descending toward deterioration" },
        { label: "C", text: "When baseline is ascending toward improvement" },
        { label: "D", text: "When practice effects have been demonstrated" },
      ],
      answer: "C",
      explanation: "An IV should not be introduced when the baseline is already moving in the desired direction; the change cannot be attributed to the IV.",
      topic: "Baseline patterns",
      sourceHint: "The independent variable should not be introduced when an ascending or descending baseline indicates improving performance.",
    },
    {
      id: "Q4",
      question: "Which best describes a confounding variable?",
      options: [
        { label: "A", text: "An uncontrolled variable that exerts unknown influence on the DV." },
        { label: "B", text: "A variable held constant by the experimenter." },
        { label: "C", text: "The manipulated variable in the experiment." },
        { label: "D", text: "The measurement system used." },
      ],
      answer: "A",
      explanation: "Confounding variables threaten internal validity by influencing the DV in ways the experimenter has not controlled.",
      topic: "Validity",
      sourceHint: "Confounding variables exert unknown or uncontrolled influences on the dependent variable.",
    },
    {
      id: "Q5",
      question: "How many basic kinds of IV change can an experimenter make?",
      options: [
        { label: "A", text: "One" },
        { label: "B", text: "Two: introduce a new condition or reinstate an old one" },
        { label: "C", text: "Three: introduce, reinstate, or randomize" },
        { label: "D", text: "Four: A, B, A-B, multielement" },
      ],
      answer: "B",
      explanation: "Despite the infinite design space, an experimenter can only INTRODUCE a new condition or REINSTATE an old one.",
      topic: "Experimental design",
      sourceHint: "an experimenter can make only two basic kinds of independent variable changes: introduce a new condition, or reinstate an old condition.",
    },
    {
      id: "Q6",
      question: "Which is the logical structure underlying single-case inference?",
      options: [
        { label: "A", text: "Affirmation of the consequent" },
        { label: "B", text: "Affirmation of the antecedent" },
        { label: "C", text: "Modus tollens" },
        { label: "D", text: "Bayesian updating" },
      ],
      answer: "A",
      explanation: "Single-case logic relies on affirmation of the consequent (if A then B; B occurs; therefore probably A) - logically weak but methodologically powerful when many such predictions converge.",
      topic: "Inductive logic",
      sourceHint: "The predictive power of steady state responding enables a form of inductive reasoning known as affirmation of the consequent.",
    },
    {
      id: "Q7",
      question: "External validity refers to:",
      options: [
        { label: "A", text: "Whether the IV caused the DV change." },
        { label: "B", text: "Whether results generalize to other subjects, settings, or behaviors." },
        { label: "C", text: "Whether observers agreed." },
        { label: "D", text: "Whether the dependent variable was reliable." },
      ],
      answer: "B",
      explanation: "External validity is the extent to which a study's results can be generalized beyond the specific subjects, settings, and behaviors observed.",
      topic: "Validity",
      sourceHint: "External validity refers to the degree to which a study's results are generalizable to other subjects, settings, and/or behaviors.",
    },
  ],
  reviewPrompts: [
    "State the research question, IV, DV, and key extraneous variables for a study you know.",
    "Walk through baseline logic (prediction, verification, replication) on a hypothetical case.",
    "Identify a confounding variable in a recent intervention and explain how to manage it.",
    "Describe a behavior whose baseline you would expect to be variable and what you would do before introducing the IV.",
    "Explain why affirmation of the consequent is logically invalid but methodologically usable.",
    "Sketch an experimental design that addresses both internal and external validity.",
  ],
  recap: [
    "Measurement reveals what changed; experimentation reveals WHY. ABA establishes functional relations via single-case designs where each subject serves as their own control.",
    "Baseline logic - prediction, verification, replication - is the inductive engine of single-case designs. Introduce the IV only when the baseline supports prediction; verify by demonstrating the counterfactual; replicate to rule out alternative explanations.",
    "Internal validity (IV-caused) and external validity (generalizable) are two distinct virtues. Confounding variables threaten internal validity; systematic replication establishes external validity.",
  ],
  sourceExcerpts: [
    {
      heading: "Experimental control",
      text: "Experimental control is achieved when a predictable change in behavior (the dependent variable) is reliably produced by the systematic manipulation of some aspect of the environment (the independent variable). An analysis of a behavior has been achieved when a reliable functional relation between the behavior and some specified aspect of the environment has been demonstrated convincingly.",
    },
    {
      heading: "Baseline logic",
      text: "Steady state responding enables a form of inductive reasoning called baseline logic. Baseline logic entails three elements: prediction, verification, and replication. Verification is accomplished by demonstrating that the prior level of baseline responding would have remained unchanged had the independent variable not been introduced. Replication within an experiment means reproducing a previously observed behavior change by reintroducing the independent variable.",
    },
    {
      heading: "Four baseline patterns",
      text: "The independent variable should be introduced when stable baseline responding has been achieved. The independent variable should not be introduced when an ascending or descending baseline indicates improving performance. The independent variable should be introduced when an ascending or a descending baseline indicates deteriorating performance. An independent variable should not be imposed on a highly variable, unstable baseline.",
    },
    {
      heading: "Two basic IV changes",
      text: "Although an infinite number of experimental designs are possible as a result of the many ways independent variables can be manipulated and combined, an experimenter can make only two basic kinds of independent variable changes: introduce a new condition, or reinstate an old condition.",
    },
  ],
};

export default chapter07Module;
